import type { LocationQueryValue } from 'vue-router'

import type {
  AnalyticsCreateEventParams,
  YandexMetrikaParamsData,
  EccommerceDefaultParams,
  EventDataShort,
  EventDataFull,
  EcommerceData,
  AnalyticsCreateEcommerceParams,
  EcommerceDataParamsFull,
  Activity,
  Event,
  Location,
  YandexMetrikaUserData,
  YandexMetrikaParamsDataItem,
  UtmAnalytics,
  UtmAnalyticsLocalStorage,
  WidgetAdvertisingCounters,
  WidgetYmEvent,
} from '@/models'

export default defineNuxtPlugin((_nuxtApp) => {
  const $device = useDevice()
  const { yandexMetrikaId, version, isWidget } = useRuntimeConfig().public
  const { currentLocation } = useLocationStore()
  const isDev = process.env.NODE_ENV === 'development'

  const checkIsDebug = (): boolean => {
    const debugEnable = useDebugStore()

    return debugEnable.debugFeatures.showAnalytics || isDev
  }
  /**
   * возвращает куку _ga ,которую устанавливает Google Tag Manager, для client_id
   */
  const getClientId = (): string | null => {
    // получаем из куки _ga
    // на деве не будет работать, тк эта кука ставится на domain='.kassir.ru'
    const cookie = useCookie<string>('_ga')
    const regExp = /(\d{4,}).(\d{4,})/gm

    if (cookie.value) {
      const execValues = regExp.exec(cookie.value)

      return execValues?.length ? execValues[0] : null
    }

    return null
  }

  class Analytics {
    protected readonly isDev: boolean
    protected readonly $device: typeof $device
    protected readonly $currentLocation: Location
    protected readonly version: string

    constructor(
      device: typeof $device,
      currentLocation: Location,
      version: string,
      isDev: boolean,
    ) {
      this.$device = device
      this.$currentLocation = currentLocation
      this.version = version
      this.isDev = isDev
    }

    /**
     * возращает id юзера
     */
    protected getUserId(): number | null {
      const authStore = useAuthStore()

      return authStore.currentUser?.id || null
    }

    /**
     * пушит событие в gtm
     * @param data
     * @protected
     */
    protected trackGtmEvent(data: any) {
      // if (isWidget) return // закомментировано, т.к. KSR-3490 Андрей Потоцкий сказал что нужны события на виджете
      if (!checkIsDebug()) {
        try {
          window.dataLayer && window.dataLayer.push(data)
        } catch (e) {
          console.warn('fail to push gtm')
        }
      } else {
        console.log('gtm ', data)
      }
    }

    // TODO: тут не очень понимаю, как отделить сторы
    // и композаблы в целом, тк получать значение
    // из стора нужно в момент вызова функции,
    // а не при ините плагина
    // вернемся, когда будем писать юнит-тесты
    protected getActivity(): Undefinable<Activity> {
      return useActivityStore().activity
    }

    protected getEvent(): Undefinable<Event> {
      return useEventStore().event
    }
  }

  class AnalyticsBase extends Analytics {
    private get requiredValues() {
      return {
        version: this.$device.isDesktop ? 'Полная' : 'Мобильная',
        domain: this.$currentLocation?.name,
      }
    }

    /**
     * создает обычное событие
     * @param createObj
     */
    public create(createObj: AnalyticsCreateEventParams): void {
      // if (isWidget) return // закомментировано, т.к. KSR-3490 Андрей Потоцкий сказал что нужны события на виджете

      const activity = this.getActivity()
      const event = this.getEvent()
      const data = this.createBaseEventData(createObj, activity, event)

      this.trackGtmEvent(data)
    }

    /**
     * создает даныне для события
     * @param createObj
     * @param activity
     * @param event
     * @private
     */
    protected createBaseEventData(
      createObj: AnalyticsCreateEventParams,
      activity?: Activity,
      event?: Event,
    ) {
      let data = {
        event: createObj.event,
        ...(createObj.value || []),
        site_version: version,
      }

      data = this.addRequiredValues(createObj, data)
      data = this.addEventData(createObj, data, activity, event)

      return data
    }

    /**
     * получает название подкатегории мероприятия
     * @param activity
     */
    private getSubcategoryName(activity?: Activity) {
      const { findCategoryByParentIdDeep } = useCategories()

      return (
        activity?.extraCategories?.find((category) =>
          findCategoryByParentIdDeep(category, activity?.category.id),
        )?.name || undefined
      )
    }

    /**
     * возвращает короткую версию данных по событию
     */
    private getEventDataShort(
      activity?: Activity,
      event?: Event,
    ): EventDataShort {
      const subcategoryName = this.getSubcategoryName(activity)

      return {
        e_name: activity?.title || '',
        e_id: activity?.id || 0,
        category_name: activity?.category.name,
        category_id: activity?.category.id,
        subcategory_name: subcategoryName,
        venue_name: event?.venueName,
        venue_id: event?.venue?.id,
        e_date: event?.beginsAt,
      }
    }

    /**
     * возвращает полную версию данных по событию
     */
    private getEventDataFull(
      activity?: Activity,
      event?: Event,
    ): EventDataFull {
      return {
        ...this.getEventDataShort(activity, event),
        date_from: activity?.dateRange?.beginsAt,
        date_to: activity?.dateRange?.endsAt,
        min_price: activity?.priceRange?.min,
        max_price: activity?.priceRange?.max,
        timestamp: Date.now(),
      }
    }

    /**
     * добавляет дефолтные значения в событие
     * @param createObj
     * @param data
     * @private
     */
    private addRequiredValues(
      createObj: AnalyticsCreateEventParams,
      data: any,
    ) {
      if (createObj.isRequiredValuesNeed) {
        data = {
          ...data,
          ...this.requiredValues,
          clientid: getClientId(),
          userid: this.getUserId(),
        }
      }

      return data
    }

    /**
     * добавляет данные из события (Event)
     * @param createObj
     * @param data
     * @param activity
     * @param event
     * @private
     */
    private addEventData(
      createObj: AnalyticsCreateEventParams,
      data: any,
      activity?: Activity,
      event?: Event,
    ) {
      if (createObj.eventData) {
        const eventData =
          createObj.eventData === 'short'
            ? this.getEventDataShort(activity, event)
            : this.getEventDataFull(activity, event)

        data = {
          ...data,
          ...eventData,
        }
      }

      return data
    }
  }

  class AnalyticsYandexMetrika extends AnalyticsBase {
    private readonly yandexMetrikaId: string
    constructor(
      device: typeof $device,
      currentLocation: Location,
      version: string,
      isDev: boolean,
      yandexMetrikaId: string,
    ) {
      super(device, currentLocation, version, isDev)
      this.yandexMetrikaId = yandexMetrikaId
    }

    /**
     * создает событие для yandexMetrika
     * @param createObj
     * @param data
     * @private
     */
    public override create(createObj: AnalyticsCreateEventParams) {
      // if (isWidget) return // закомментировано, т.к. KSR-3490 Андрей Потоцкий сказал что нужны события на виджете

      if (createObj.yandexMetrika) {
        const activity = this.getActivity()
        const event = this.getEvent()
        const data = this.createBaseEventData(createObj, activity, event)
        const yandexMetrikaParamsData: YandexMetrikaParamsData =
          this.createYandexMetrikaParams(createObj, data)
        const yandexMetrikaUserData: YandexMetrikaUserData =
          this.createYandexMetrikaUserParams()

        this.trackYandexMetrikaEvent(
          yandexMetrikaParamsData,
          yandexMetrikaUserData,
        )
      }
    }

    /**
     * возвращает данные по юзеру
     * @private
     */
    private createYandexMetrikaUserParams(): YandexMetrikaUserData {
      return {
        UserID: this.getUserId(),
        clientid: getClientId(),
      }
    }

    /**
     * трансформирует данные в формат для яндекс метрики
     * @param createObj
     * @param data
     * @private
     */
    private createYandexMetrikaParams(
      createObj: AnalyticsCreateEventParams,
      data: any,
    ) {
      const yandexMetrikaParamsData: YandexMetrikaParamsData = {}

      yandexMetrikaParamsData[createObj.event] = [{ site_version: version }]
      yandexMetrikaParamsData[createObj.event] = Object.entries(data)
      yandexMetrikaParamsData[createObj.event] =
        this.addFieldsInYandexMetrikaParams(
          yandexMetrikaParamsData[createObj.event],
        )
      yandexMetrikaParamsData[createObj.event] =
        this.filterExcessFieldsInYandexMetrikaParams(
          yandexMetrikaParamsData[createObj.event],
        )

      return yandexMetrikaParamsData
    }

    /**
     * добавляет параметры в событие яндекс метрики
     * @param yandexMetrikaParamsDataItem
     * @private
     */
    private addFieldsInYandexMetrikaParams(
      yandexMetrikaParamsDataItem: YandexMetrikaParamsDataItem,
    ) {
      return yandexMetrikaParamsDataItem.map((item: [string, any]) => {
        const value = {}

        Object.defineProperty(value, item[0], {
          value: item[1],
          writable: true,
          enumerable: true,
        })

        return value
      })
    }

    /**
     * убирает ненужные поля из даты для яндекс метрики
     * эти поля передаются в yandexMetrikaUserData
     * event задает ключ массиву параметров
     * @param yandexMetrikaParamsDataItem
     * @private
     */
    private filterExcessFieldsInYandexMetrikaParams(
      yandexMetrikaParamsDataItem: YandexMetrikaParamsDataItem,
    ) {
      return yandexMetrikaParamsDataItem.filter((item: any) => {
        const key: string = Object.keys(item)[0]

        return !(
          key === 'clientid' ||
          key === 'userid' ||
          key === 'event' ||
          key === 'isTrusted'
        )
      })
    }

    /**
     * пушит события в яндекс метрику
     * @param yandexMetrikaParamsData
     * @param yandexMetrikaUserData
     * @private
     */
    private trackYandexMetrikaEvent(
      yandexMetrikaParamsData: YandexMetrikaParamsData,
      yandexMetrikaUserData: YandexMetrikaUserData,
    ) {
      if (!checkIsDebug()) {
        try {
          // тут только так. официальный плагин не завелся
          window.ym &&
            window.ym(this.yandexMetrikaId, 'params', yandexMetrikaParamsData)
          window.ym &&
            window.ym(this.yandexMetrikaId, 'userParams', yandexMetrikaUserData)
        } catch (e) {
          console.warn('fail to push ym')
        }
      } else {
        console.log('ym params ', yandexMetrikaParamsData)
        console.log('ym user ', yandexMetrikaUserData)
      }
    }
  }

  class AnalyticsEcommerce extends Analytics {
    /**
     * создает eCommerce событие
     * @param createObj
     */
    public create(createObj: AnalyticsCreateEcommerceParams) {
      // if (isWidget) return // закомментировано, т.к. KSR-3490 Андрей Потоцкий сказал что нужны события на виджете

      const activity = this.getActivity()
      const event = this.getEvent()
      const ecommerceFullData = this.getEcommerceFullData(
        createObj,
        activity,
        event,
      )

      this.trackGtmEvent(ecommerceFullData)
    }

    /**
     * собирает данные для ecommerce события
     * @param createObj
     * @param activity
     * @param event
     * @private
     */
    private getEcommerceFullData(
      createObj: AnalyticsCreateEcommerceParams,
      activity?: Activity,
      event?: Event,
    ): EcommerceData {
      return {
        ...createObj.customer,
        ga_cookie: useCookie<string>('_ga', { sameSite: 'lax' }).value,
        domain: this.$currentLocation?.domain,
        event: createObj.event,
        ecommerce: {
          ...(createObj.ecommerce || []),
          items: this.getEcommerceData(createObj, activity, event),
        },
        version: $device.isDesktop ? 'Полная' : 'Мобильная',
        clientid: getClientId(),
        userid: this.getUserId(),
      }
    }

    /**
     * возвращает обязательные для всех событий поля
     */
    private getEcommerceDefault(
      activity?: Activity,
      event?: Event,
    ): EccommerceDefaultParams {
      return {
        item_name: activity?.title,
        item_id: activity?.id,
        item_category: activity?.category.name,
        item_category2: activity?.category.parent?.name,
        item_brand: event?.venueName,
        date_from: activity?.dateRange?.beginsAt,
        date_to: activity?.dateRange?.endsAt,
        min_price: activity?.priceRange?.min,
        max_price: activity?.priceRange?.max,
        timestamp: Date.now(),
      }
    }

    /**
     * возвращает массив событий
     */
    private getEcommerceData(
      createObj: AnalyticsCreateEcommerceParams,
      activity?: Activity,
      event?: Event,
    ): EcommerceDataParamsFull[] {
      const ecommerceData: any[] = []

      if (createObj?.value?.length) {
        createObj?.value?.forEach((item) => {
          ecommerceData.push({
            ...this.getEcommerceDefault(activity, event),
            ...item,
          })
        })
      } else {
        ecommerceData.push({
          ...this.getEcommerceDefault(activity, event),
        })
      }

      return ecommerceData
    }
  }

  class AnalyticsUtm {
    private _utm: Undefinable<UtmAnalytics>
    private ttlUtm = getMilliseconds.inDays(90)
    private aeroflotLoyaltyKey = 'aeroflot'
    private s7LoyaltyKey = 's7'

    public get utm() {
      return {
        ...this._utm,
        googleClientId: this.getGoogleClientId() || undefined,
        yandexClientId: this.getYandexClientId() || undefined,
      }
    }

    public get isAeroflotUtm() {
      return this.utm.utmSource === this.aeroflotLoyaltyKey
    }

    public get isS7Utm() {
      return this.utm.utmSource === this.s7LoyaltyKey
    }

    /**
     * устнававливает utm метки
     */
    public setUtm() {
      const utm =
        this.getUtmFromUrl() || this.getUtmFromLocalStorage() || undefined

      if (utm) {
        this._utm = utm
      }
    }

    public clearUtm() {
      localStorage.removeItem('utm')
    }

    /**
     * устанавливает метки в localStorage на сутки
     * @param utm
     * @private
     */
    private setUtmInLocalStorage(utm: UtmAnalytics) {
      const value: UtmAnalyticsLocalStorage = {
        ...utm,
        expiry: new Date().getTime() + this.ttlUtm,
      }

      utm && localStorage.setItem('utm', JSON.stringify(value))
    }

    /**
     * получает utmМетки из localStorage
     * @private
     */
    private getUtmFromLocalStorage(): Undefinable<UtmAnalytics> {
      const utmString = localStorage.getItem('utm')

      if (!utmString) return

      const utmParsed: UtmAnalyticsLocalStorage = JSON.parse(utmString)

      if (this.checkUtmIsExpire(utmParsed)) return

      return utmParsed
    }

    /**
     * проверяет, что сохраненные метки валидны
     * @param utm
     * @private
     */
    private checkUtmIsExpire(utm: UtmAnalyticsLocalStorage): boolean {
      if (new Date().getTime() > utm.expiry) {
        this.deleteUtmFromLocalStorage()

        return true
      }

      return false
    }

    /**
     * удаляет utm из localStorage
     */

    private deleteUtmFromLocalStorage() {
      localStorage.removeItem('utm')
    }

    /**
     * берет метки из url
     * @private
     */
    private getUtmFromUrl(): Undefinable<UtmAnalytics> {
      const { query } = useRoute()
      const {
        utmSource,
        utm_source: utmSourceSnakeCase,
        utmMedium,
        utm_medium: utmMediumSnakeCase,
        utmCampaign,
        utm_campaign: utmCampaignSnakeCase,
        googleClientId,
        browserLocale,
        utmTerm,
        utm_term: utmTermSnakeCase,
        utmContent,
        utm_content: utmContentSnakeCase,
      } = query

      // нужно не давать банкам писать свои урлы. требование бизнеса
      const regexp =
        /acs2.multicarta.ru|securepayments.tinkoff.ru|securepay.tinkoff.ru?/gi
      const getValueFromParam = (
        param: LocationQueryValue | LocationQueryValue[],
      ) => {
        if (Array.isArray(param) || !param) return

        return (!regexp.test(param) && param) || undefined
      }

      if (
        utmSource ||
        utmMedium ||
        utmCampaign ||
        googleClientId ||
        browserLocale ||
        utmTerm ||
        utmContent ||
        utmSourceSnakeCase ||
        utmMediumSnakeCase ||
        utmCampaignSnakeCase ||
        utmContentSnakeCase ||
        utmTermSnakeCase
      ) {
        const utm: UtmAnalytics = {
          utmSource: getValueFromParam(utmSource || utmSourceSnakeCase),
          utmMedium: getValueFromParam(utmMedium || utmMediumSnakeCase),
          utmCampaign: getValueFromParam(utmCampaign || utmCampaignSnakeCase),
          utmTerm: getValueFromParam(utmTerm || utmTermSnakeCase),
          utmContent: getValueFromParam(utmContent || utmContentSnakeCase),
          googleClientId: undefined,
          yandexClientId: undefined,
          browserLocale: (browserLocale as string) || 'ru',
        }

        this.setUtmInLocalStorage(utm)

        return utm
      }

      return undefined
    }

    /**
     * возвращает куку _ga ,которую устанавливает Google Tag Manager
     */
    private getGoogleClientId() {
      // получаем из куки _ga
      // на деве не будет работать, тк эта кука ставится на domain='.kassir.ru'
      const cookie = useCookie<string>('_ga')
      const regExp = /(\d{4,}).(\d{4,})/gm

      if (cookie.value) {
        const execValues = regExp.exec(cookie.value)

        return execValues?.length ? execValues[0] : null
      }

      return null
    }

    /**
     * возвращает куку yandexuid, которую устанваливает yandexMetrika
     * @private
     */
    private getYandexClientId() {
      const cookie = useCookie<string>('yandexuid')

      return cookie.value || null
    }
  }

  class WidgetAnalyticsSingleton {
    // eslint-disable-next-line no-use-before-define
    private static instance: WidgetAnalyticsSingleton
    private readonly counters: WidgetAdvertisingCounters

    constructor(counters: WidgetAdvertisingCounters) {
      this.counters = counters

      if (WidgetAnalyticsSingleton.instance) {
        return WidgetAnalyticsSingleton.instance
      }

      WidgetAnalyticsSingleton.instance = this

      return WidgetAnalyticsSingleton.instance
    }

    /**
     * инитит счетчики
     */
    public initWidgetScripts() {
      if (!isWidget) return

      this.initGtm()
      this.initGtm4()
      this.initYm()
      this.initGa()
      this.initWidgetGtm()
    }

    /**
     * вызывает события для виджета
     * @private
     */
    public trackYmEvent(event: WidgetYmEvent) {
      if (!window.ym || !this.counters.ym?.length) return

      if (!checkIsDebug()) {
        try {
          this.counters.ym.forEach((counter) => {
            window.ym && window.ym(counter, 'reachGoal', event)
            console.log('ym widget ', event)
          })
        } catch (e) {
          console.warn('fail to push ym widget')
        }
      } else {
        console.log('ym widget ', event)
      }
    }

    private initGtm() {
      if (!this.counters.gtm) return

      const scriptString = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})
      (window,document,"script","dataLayer", "${this.counters.gtm}");`

      this.createScript({
        innerHTML: scriptString,
      })
    }

    private initGtm4() {
      if (!this.counters.gtm4) return

      const initSrc = `https://www.googletagmanager.com/gtag/js?id=${this.counters.gtm4}`
      const scriptString = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config', '${this.counters.gtm4}');`

      this.createScript({
        async: true,
        src: initSrc,
      })

      this.createScript({
        innerHTML: scriptString,
      })
    }

    private initGa() {
      if (!this.counters.ga?.length) return
      const scriptString = `(function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){(i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)})(window,document,'script','//www.google-analytics.com/analytics.js','ga');`

      this.createScript({
        innerHTML: scriptString,
      })

      this.counters.ga.forEach((counter) => {
        if (!window.ga) return
        window.ga('create', counter, 'auto', `${getClientId()}`)
        window.ga('send', 'pageview')
      })
    }

    private initYm() {
      if (!this.counters.ym?.length) return

      const script = `(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
      m[i].l=1*new Date();
      for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
      k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
      (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");`

      this.createScript({
        innerHTML: script,
      })

      this.counters.ym.forEach((counter, index) => {
        if (!window.ym) return
        window.ym(counter, 'init', {
          clickmap: true,
          trackLinks: true,
          accurateTrackBounce: true,
          webvisor: index === 0,
          trackHash: true,
          ecommerce: 'dataLayer',
        })
      })
    }

    private initWidgetGtm() {
      if (!this.counters.widgetGtmId) return

      const scriptString = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${this.counters.widgetGtmId}')`

      this.createScript({
        innerHTML: scriptString,
      })
    }

    private createScript(data: Partial<HTMLScriptElement>) {
      const script = document.createElement('script')

      Object.entries(data).forEach(([attribute, value]) => {
        switch (attribute) {
          case 'innerHTML':
            value && (script.innerHTML = value as string)
            break
          case 'async':
            value && (script.async = value as boolean)
            break
          case 'src':
            value && (script.src = value as string)
        }
      })
      script.type = 'text/javascript'

      document.head.appendChild(script)
    }
  }

  return {
    provide: {
      analytics: {
        base: new AnalyticsBase($device, currentLocation!, version, isDev),
        ecommerce: new AnalyticsEcommerce(
          $device,
          currentLocation!,
          version,
          isDev,
        ),
        yandexMetrika: new AnalyticsYandexMetrika(
          $device,
          currentLocation!,
          version,
          isDev,
          yandexMetrikaId,
        ),
        utm: new AnalyticsUtm(),
        widget: (counters: WidgetAdvertisingCounters) =>
          new WidgetAnalyticsSingleton(counters),
      },
    },
  }
})
